import React from "react";
import { motion } from "framer-motion";
import { C, ACCENT_GRADIENT, SHADOWS, MESH_BLUE } from "./tokens";

const EASE = [0.4, 0, 0.2, 1] as const;

export function GlassCard({ children, className = "", onClick, hover = true }: {
  children: React.ReactNode; className?: string; onClick?: () => void; hover?: boolean;
}) {
  return (
    <motion.div
      whileHover={hover ? { y: -3 } : undefined}
      transition={{ duration: 0.3, ease: EASE }}
      onClick={onClick}
      className={`relative overflow-hidden rounded-none border ${className}`}
      style={{ background: "rgba(245,239,228,0.72)", borderColor: "rgba(28,25,23,0.10)", boxShadow: SHADOWS.premium }}
    >
      {children}
    </motion.div>
  );
}

export function GlowCard({ children, className = "", onClick, glowColor = C.accent }: {
  children: React.ReactNode; className?: string; onClick?: () => void; glowColor?: string;
}) {
  return (
    <motion.div
      whileHover={{ y: -4 }}
      transition={{ duration: 0.35, ease: EASE }}
      onClick={onClick}
      className={`relative overflow-hidden rounded-none border bg-white ${className}`}
      style={{ borderColor: "rgba(28,25,23,0.10)", boxShadow: `0 4px 24px -8px ${glowColor}22, 0 1px 3px rgba(28,25,23,0.04)` }}
    >
      <div className="absolute -top-px left-6 right-6 h-px" style={{ background: `linear-gradient(90deg, transparent, ${glowColor}55, transparent)` }} />
      {children}
    </motion.div>
  );
}

export function GradientBorderCard({ children, className = "", gradient = ACCENT_GRADIENT }: {
  children: React.ReactNode; className?: string; gradient?: string;
}) {
  return (
    <div className={`relative rounded-none p-[1.5px] ${className}`} style={{ background: gradient }}>
      <div className="rounded-none bg-white h-full w-full">
        {children}
      </div>
    </div>
  );
}

export function PremiumCard({ children, className = "", onClick, accent }: {
  children: React.ReactNode; className?: string; onClick?: () => void; accent?: string;
}) {
  return (
    <motion.div
      whileHover={{ y: -2 }}
      transition={{ duration: 0.3, ease: EASE }}
      onClick={onClick}
      className={`relative overflow-hidden rounded-none border bg-white ${className}`}
      style={{ borderColor: "rgba(28,25,23,0.10)", boxShadow: SHADOWS.premium }}
    >
      {accent && <div className="absolute top-0 left-0 right-0 h-[3px]" style={{ background: accent }} />}
      {children}
    </motion.div>
  );
}

export function MeshCard({ children, className = "", mesh = MESH_BLUE, onClick }: {
  children: React.ReactNode; className?: string; mesh?: string; onClick?: () => void;
}) {
  return (
    <motion.div
      whileHover={{ y: -4 }}
      transition={{ duration: 0.35, ease: EASE }}
      onClick={onClick}
      className={`relative overflow-hidden rounded-none ${className}`}
      style={{ background: mesh, boxShadow: SHADOWS.glow }}
    >
      <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: "radial-gradient(circle, #fff 1px, transparent 1px)", backgroundSize: "24px 24px" }} />
      {children}
    </motion.div>
  );
}

export function StatTile({ label, value, sub, icon: Icon, accent, compact = false, gradient }: {
  label: string; value: string; sub?: string;
  icon: React.ComponentType<{ className?: string; style?: React.CSSProperties }>;
  accent: string; compact?: boolean; gradient?: string;
}) {
  return (
    <PremiumCard accent={accent}>
      <div className="p-4 sm:p-5">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <p className="text-[10px] sm:text-[11px] font-semibold uppercase tracking-widest truncate" style={{ color: C.textMuted }}>{label}</p>
            <p className="text-xl sm:text-2xl font-bold mt-1 tracking-tight truncate" style={{ color: C.text }}>{value}</p>
            {sub && <p className="text-[10px] sm:text-xs mt-1 font-medium truncate" style={{ color: C.textMid }}>{sub}</p>}
          </div>
          <div className="h-10 w-10 sm:h-11 sm:w-11 rounded-none flex items-center justify-center shrink-0" style={{ background: gradient ?? `${accent}14` }}>
            <Icon className="h-4 w-4 sm:h-5 sm:w-5" style={{ color: gradient ? "#fff" : accent }} />
          </div>
        </div>
      </div>
    </PremiumCard>
  );
}

export function ProgressRing({ value, size = 64, stroke = 6, color = C.accent, label }: {
  value: number; size?: number; stroke?: number; color?: string; label?: string;
}) {
  const radius = (size - stroke) / 2;
  const circ = 2 * Math.PI * radius;
  const offset = circ - (value / 100) * circ;
  return (
    <div className="relative inline-flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke={C.border} strokeWidth={stroke} opacity={0.25} />
        <motion.circle
          cx={size / 2} cy={size / 2} r={radius} fill="none" stroke={color} strokeWidth={stroke}
          strokeLinecap="round" strokeDasharray={circ}
          initial={{ strokeDashoffset: circ }}
          animate={{ strokeDashoffset: offset }}
          transition={{ duration: 1.2, ease: "easeOut" }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-sm font-bold" style={{ color: C.text }}>{Math.round(value)}%</span>
        {label && <span className="text-[8px] font-medium" style={{ color: C.textMuted }}>{label}</span>}
      </div>
    </div>
  );
}

export function SectionHeader({ title, subtitle, icon: Icon, iconBg, action }: {
  title: string; subtitle?: string;
  icon: React.ComponentType<{ className?: string; style?: React.CSSProperties }>;
  iconBg: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between gap-3 px-5 sm:px-6 py-4 sm:py-5 border-b" style={{ borderColor: "rgba(28,25,23,0.08)" }}>
      <div className="flex items-center gap-3 min-w-0">
        <div className="h-10 w-10 sm:h-11 sm:w-11 rounded-none flex items-center justify-center shrink-0" style={{ background: iconBg }}>
          <Icon className="h-5 w-5 sm:h-[22px] sm:w-[22px]" />
        </div>
        <div className="min-w-0">
          <h3 className="text-base sm:text-lg font-semibold tracking-tight truncate" style={{ color: C.text }}>{title}</h3>
          {subtitle && <p className="text-xs sm:text-sm truncate mt-0.5" style={{ color: C.textMuted }}>{subtitle}</p>}
        </div>
      </div>
      {action}
    </div>
  );
}

export function MiniBar({ value, max, color = C.accent }: { value: number; max: number; color?: string }) {
  const pct = max > 0 ? (value / max) * 100 : 0;
  return (
    <div className="h-2 rounded-none overflow-hidden" style={{ background: C.bg }}>
      <motion.div
        className="h-full rounded-none"
        style={{ background: `linear-gradient(90deg, ${color}, ${color}cc)` }}
        initial={{ width: 0 }}
        animate={{ width: `${pct}%` }}
        transition={{ duration: 1, ease: "easeOut" }}
      />
    </div>
  );
}

export function Pill({ children, color, bg }: { children: React.ReactNode; color: string; bg?: string }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-none px-2.5 py-0.5 text-[11px] font-semibold" style={{ background: bg ?? `${color}15`, color }}>
      {children}
    </span>
  );
}

export function Avatar({ name, color, size = 56 }: { name: string; color: string; size?: number }) {
  const initials = name.split(" ").map(n => n[0]).join("").slice(0, 2);
  return (
    <div
      className="rounded-none flex items-center justify-center font-semibold text-white shrink-0"
      style={{
        width: size, height: size, fontSize: size * 0.38,
        background: `linear-gradient(135deg, ${color} 0%, ${color}cc 100%)`,
        boxShadow: `0 2px 8px ${color}30`,
      }}
    >
      {initials}
    </div>
  );
}
